import React from 'react';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../types';
import defaultStyles from '../../../styles/defaults.scss';
import styles from './compareShareBookmarkMobile.scss';
import Cross from '../../icons/cross';
import MoreIcon from '../../icons/moreIcon';
import ShareIcon from '../../icons/shareIcon';
import FragmentPlaceholder from '../../fragmentPlaceholder/fragmentPlaceholder';
import CompareBtn from '../compareShareBookmark/compareBtn';
import classNames from 'classnames';
import LogoService from '../../icons/logoservice';
/* eslint-disable @typescript-eslint/no-non-null-assertion */

const breakpointMobileExtraSmall = parseInt(defaultStyles.breakpointMobileExtraSmall);
const parsedButtonHeight = parseInt(styles.buttonHeight);
const buttonHeight = !isNaN(parsedButtonHeight) ? parsedButtonHeight : 0;

const CompareShareBookmarkMobile = inject('store')(
    observer(({ store }: IInjectedProps) => {
        if (!store) {
            return null;
        }

        const { dataStore, orderModuleStore, showKioskLayout, localization, fetchedData, articleSoldOut } = { ...store };
        
        const refPersonaliseButton = React.useRef<HTMLButtonElement>(null);
        const seoSlug = dataStore.breadcrumb.category.seoSlug ?? '';
        const savKey = dataStore.orderModule.articleInfo.salesArticleVariantKey ?? '';
        const mpcItemOrigin = orderModuleStore.mpcItemOrigin ?? '';
        const generalL10n = localization.generalL10n;
        const logoCreatorL10n = localization.logoCreatorL10n;
        const expanded = orderModuleStore.compareShareBookmarkMobileExpanded;
        const [buttonCount, setButtonCount] = React.useState(4);

        const logoCreatorEnabled = dataStore.contextData.featureToggles.enableLogoCreator;
        const isSmakeDesignerAvailable =
            dataStore.shutterModule.productInfo?.description?.isSmakeDesignerAvailable ?? false;
        const logoServiceRedirectUrl = dataStore.bannerModule.logoServiceRedirectUrl;
        const logoServiceInfoUrl =
            fetchedData.shutterModule.productInfo.description.links.logoServiceInfoUrl;
        const shouldShowLogoCreatorButton =
            logoCreatorEnabled &&
            !showKioskLayout &&
            (isSmakeDesignerAvailable || logoServiceRedirectUrl);
        const logoCreatorButtonId = isSmakeDesignerAvailable ? 'logo-creator-button' : 'no-logo-creator-button';

        const isExtraSmall = typeof window !== 'undefined' ? window.innerWidth <= breakpointMobileExtraSmall : false;

        React.useEffect(() => {
            const container = document.getElementById('compareShareBookmark-mobile-container');
            if (container) {
                const buttons = container.getElementsByTagName('BUTTON');
                let visibleButtonCount = buttons.length;

                if (refPersonaliseButton && refPersonaliseButton.current && isExtraSmall) {
                    visibleButtonCount--;
                }

                setButtonCount(visibleButtonCount > 0 ? visibleButtonCount : 0);
            }
        }, [isExtraSmall]);

        const handleExpandClick = React.useCallback(() => {
            orderModuleStore.toggleCompareShareBookmarkMobileExpanded();
        }, [expanded]);

        const handleShareClick = React.useCallback(() => {
            if (navigator.share) {
                navigator.share({
                    title: generalL10n.shareLinkText.replace(
                        '{0}',
                        dataStore.orderModule.articleInfo.title ?? ''
                    ),
                    text: generalL10n.shareLinkBody
                        .replace('{0}', dataStore.orderModule.articleInfo.title ?? '')
                        .replace('{1}', generalL10n.companyNameSimple),
                    url: window.location.href,
                });
            } else {
                window.location.href =
                    'mailto:?subject=' +
                    encodeURIComponent(
                        generalL10n.shareLinkText.replace('{0}', dataStore.orderModule.articleInfo.title ?? '')
                    ) +
                    '&body=' +
                    encodeURIComponent(
                        generalL10n.shareLinkBody
                            .replace('{0}', dataStore.orderModule.articleInfo.title ?? '')
                            .replace('{1}', generalL10n.companyNameSimple)
                    ) +
                    ' ' +
                    window.location.href.split('?')[0];
            }
        }, []);

        const handleLogoCreatorClick = () => {
            if (isSmakeDesignerAvailable) {
                if (logoServiceRedirectUrl) {
                    store.showPersonalizeOverlay();
                } else {
                    store.onPersonalizeIntent();
                }

                return;
            }

            if (logoServiceInfoUrl) {
                window.open(logoServiceInfoUrl, '_self');
            }
        };

        const expandButtonClassNames = expanded
            ? [styles.compareShareBookmark_mobile_expand_button, styles.expanded].join(' ')
            : styles.compareShareBookmark_mobile_expand_button;
        const buttonContainerClassNames = expanded
            ? [styles.compareShareBookmark_mobile_button_container, styles.expanded].join(' ')
            : styles.compareShareBookmark_mobile_button_container;

        const actionButtonsTop = -(buttonHeight * buttonCount);

        return (
            <div
                id='compareShareBookmark-mobile-container'
                data-testid='compareShareBookmark-mobile-container'
                data-semantic-id='compareShareBookmark-mobile-container'
                className={classNames(
                    styles.compareShareBookmark_mobile_container, 
                    {
                        [styles.compareShareBookmark_mobile_expand]:
                            orderModuleStore.compareShareBookmarkMobileExpanded,
                    }
                )}
            >
                <button
                    data-testid='expand-button'
                    data-semantic-id='expand-button'
                    className={expandButtonClassNames}
                    onClick={handleExpandClick}
                >
                    {expanded ? <Cross /> : <MoreIcon />}
                </button>
                <div
                    className={styles.compareShareBookmark_mobile_action_buttons}
                    style={{ top: actionButtonsTop, pointerEvents: expanded ? 'initial' : 'none' }}
                >
                    {shouldShowLogoCreatorButton && (
                        <div
                            className={classNames(
                                buttonContainerClassNames,
                                styles.personalizeButton,
                            )}
                        >
                            <button
                                data-testid={logoCreatorButtonId}
                                data-semantic-id={logoCreatorButtonId}
                                ref={refPersonaliseButton}
                                onClick={handleLogoCreatorClick}
                                className={styles.compareShareBookmark_mobile_action_button}
                            >
                                <LogoService />
                                {logoCreatorL10n!.personalise}
                            </button>
                        </div>
                    )}
                    {!showKioskLayout && (
                        <div className={buttonContainerClassNames}>
                            <button
                                data-testid='share-button' data-semantic-id='share-button'
                                onClick={handleShareClick}
                                className={styles.compareShareBookmark_mobile_action_button}
                            >
                                <ShareIcon />
                                {generalL10n.shareButtonLocale}
                            </button>
                        </div>
                    )}
                    {!articleSoldOut &&
                        <div className={buttonContainerClassNames}>
                            <CompareBtn
                                savKey={savKey}
                                seoSlug={seoSlug}
                                itemOrigin={mpcItemOrigin}
                            />
                        </div>
                    }
                    {dataStore?.contextData?.portalMandantToken != 'GLB' && (
                        <div className={buttonContainerClassNames}>
                            <FragmentPlaceholder
                                src={`/ESPP1-Bookmark/bookmark-add-button?containerId=${store?.getBookmarkContainerID}`}
                                ignoreErrors={true}
                                timeout={2}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    })
);

export default CompareShareBookmarkMobile;

